import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { addTextToVideo, checkTaskStatus, apiFetch } from '../../services/videoApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, FileUp, Type 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const AddTextToVideo = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // UI State
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [videoDuration, setVideoDuration] = useState(0);
    const [error, setError] = useState(null);

    // Tool Params
    const [text, setText] = useState('');
    const [fontSize, setFontSize] = useState(70);
    const [textColor, setTextColor] = useState('#FFFFFF');
    const [horizontalPos, setHorizontalPos] = useState('center');
    const [verticalPos, setVerticalPos] = useState('center');
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);
    
    // Process State
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    // Cleanup URLs
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [previewUrl]);

    // Handle Metadata Load
    const handleMetadataLoaded = (e) => {
        const duration = e.target.duration;
        if (duration && !isNaN(duration)) {
            setVideoDuration(duration);
            setEndTime(duration); 
        }
    };

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
                        if (payload.new_credits !== undefined) setCredits(payload.new_credits);

                        if (payload.result && payload.result.download_url) {
                            setResultUrl(payload.result.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError(t('addTextVideo.errors.generic'));
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE' || data.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('addTextVideo.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2500);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, setCredits, t]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 100 * 1024 * 1024) {
                setError(t('addTextVideo.errors.tooLarge'));
                return;
            }
            setFile(selected);
            setFileName(selected.name);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(selected));
            setError(null);
            setResultUrl(null);
            setProgressValue(0);
            // Reset duration so the hidden video triggers metadata load again
            setVideoDuration(0); 
        }
    };

    const handleClear = () => {
        setFile(null);
        setFileName('');
        setText('');
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setError(null);
        setResultUrl(null);
        setTaskId(null);
        setProgressValue(0);
        setVideoDuration(0);
        const fileInput = document.getElementById('video-upload-text');
        if (fileInput) fileInput.value = '';
    };

    const handleProcess = async () => {
        if (!file || !text) {
            setError(t('addTextVideo.errors.missingParams'));
            return;
        }
        setIsProcessing(true);
        setProgressValue(5);
        setError(null);
        
        try {
            const params = {
                text,
                fontSize,
                textColor,
                horizontalPos,
                verticalPos,
                startTime,
                endTime
            };
            const response = await addTextToVideo(file, params);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const getProgressLabel = (step) => {
        const map = {
            'downloading': t('addTextVideo.steps.downloading'),
            'processing_video': t('addTextVideo.steps.processing_video'),
            'encoding': t('addTextVideo.steps.encoding'),
            'uploading': t('addTextVideo.steps.uploading')
        };
        return map[step] || t('addTextVideo.processing');
    };

    // Render Helper: Logic is now flat inside the main render
    const renderContent = () => {
        if (resultUrl) {
            // --- RESULT VIEW ---
            return (
                <div className="max-w-4xl mx-auto text-center">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-8 flex items-center justify-center text-green-800">
                        <CheckCircle className="mr-2" /> {t('addTextVideo.success')}
                    </div>

                    <div className="rounded-lg overflow-hidden bg-black aspect-video mb-6 shadow-lg">
                        <video src={resultUrl} controls className="w-full h-full object-contain" />
                    </div>

                    <a 
                        href={resultUrl} 
                        download={`video_text_${Date.now()}.mp4`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                    >
                        <Download className="mr-2" size={20} />
                        {t('addTextVideo.download')}
                    </a>
                    
                    <button onClick={handleClear} className="block w-full mt-6 text-slate-600 hover:text-brand-orange underline">
                        {t('addTextVideo.processAnother')}
                    </button>
                </div>
            );
        }

        // --- UPLOAD VIEW ---
        return (
            <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('addTextVideo.title')}</h2>
                <p className="mt-2 text-brand-dark">{t('addTextVideo.description')}</p>
                
                <div className="mt-6 space-y-6">
                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.uploadLabel')}</label>
                        <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                            <FileUp className="h-8 w-8 text-slate-500" />
                            <label htmlFor="video-upload-text" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer hover:text-orange-600">
                                {t('addTextVideo.selectFile')}
                                <input 
                                    id="video-upload-text" 
                                    type="file" 
                                    className="sr-only" 
                                    onChange={handleFileChange} 
                                    accept=".mp4,.avi,.mov,.mkv" 
                                />
                            </label>
                            {fileName && (
                                <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                    <span className="truncate max-w-[200px]">{fileName}</span>
                                    <button onClick={handleClear}><XCircle className="h-5 w-5 text-red-500" /></button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hidden video element to get duration */}
                    {previewUrl && (
                        <video 
                            src={previewUrl} 
                            className="hidden" 
                            onLoadedMetadata={handleMetadataLoaded} 
                        />
                    )}

                    {/* Settings Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Text Input */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.textLabel')}</label>
                            <input 
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={t('addTextVideo.textPlaceholder')}
                                className="w-full p-2 border border-slate-300 rounded-md focus:border-brand-orange focus:ring-brand-orange text-brand-dark"
                            />
                        </div>

                        {/* Positions */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.hPos')}</label>
                            <select 
                                value={horizontalPos}
                                onChange={(e) => setHorizontalPos(e.target.value)}
                                className="w-full p-2 border border-slate-300 rounded-md text-brand-dark"
                            >
                                <option value="left">{t('addTextVideo.left')}</option>
                                <option value="center">{t('addTextVideo.center')}</option>
                                <option value="right">{t('addTextVideo.right')}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.vPos')}</label>
                            <select 
                                value={verticalPos}
                                onChange={(e) => setVerticalPos(e.target.value)}
                                className="w-full p-2 border border-slate-300 rounded-md text-brand-dark"
                            >
                                <option value="top">{t('addTextVideo.top')}</option>
                                <option value="center">{t('addTextVideo.center')}</option>
                                <option value="bottom">{t('addTextVideo.bottom')}</option>
                            </select>
                        </div>

                        {/* Style */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.fontSize')}: {fontSize}px</label>
                            <input 
                                type="range" min="10" max="150" value={fontSize}
                                onChange={(e) => setFontSize(parseInt(e.target.value))}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addTextVideo.color')}</label>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="color" 
                                    value={textColor}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    className="h-10 w-20 p-1 border border-slate-300 rounded"
                                />
                                <span className="text-sm text-slate-600">{textColor}</span>
                            </div>
                        </div>

                        {/* Time Range */}
                        <div className="md:col-span-2">
                             <label className="block text-sm font-medium mb-2 text-brand-dark">
                                 {t('addTextVideo.timeRange')} ({t('addTextVideo.duration')}: {videoDuration.toFixed(1)}s)
                             </label>
                             <div className="flex gap-4 items-center">
                                <div className="flex-1">
                                    <span className="text-xs text-slate-500">{t('addTextVideo.start')}: {startTime}s</span>
                                    <input 
                                        type="range" min="0" max={videoDuration || 1} step="0.1"
                                        value={startTime}
                                        disabled={!videoDuration}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            setStartTime(val);
                                            if(val > endTime) setEndTime(val);
                                        }}
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:opacity-50"
                                    />
                                </div>
                                <div className="flex-1">
                                    <span className="text-xs text-slate-500">{t('addTextVideo.end')}: {endTime}s</span>
                                    <input 
                                        type="range" min="0" max={videoDuration || 1} step="0.1"
                                        value={endTime}
                                        disabled={!videoDuration}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            setEndTime(val);
                                            if(val < startTime) setStartTime(val);
                                        }}
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:opacity-50"
                                    />
                                </div>
                             </div>
                        </div>
                    </div>

                    {error && (
                        <p className="mt-2 text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>
                    )}

                    {/* Preview Upload */}
                    {previewUrl && (
                        <div className="rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center border border-slate-300">
                            <video src={previewUrl} controls className="w-full h-full object-contain" />
                        </div>
                    )}

                    <button
                        onClick={handleProcess}
                        disabled={!file || !text || isProcessing}
                        className="w-full md:w-auto px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isProcessing ? (
                            <><Loader2 className="animate-spin mr-2" size={20} /> {t('addTextVideo.processing')}</>
                        ) : (
                            t('addTextVideo.processButton')
                        )}
                    </button>

                    {isProcessing && (
                        <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                            <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                                <span>{getProgressLabel(progressStep)}</span>
                                <span>{progressValue}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                <div className="bg-brand-orange h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                // Assuming SubscribePrompt is defined
                <SubscribePrompt /> 
            ) : user?.credits < 0 ? (
                // Assuming PurchaseCreditsPrompt is defined
                <PurchaseCreditsPrompt />
            ) : (
                renderContent()
            )}
        </div>
    );
};

export default AddTextToVideo;
